import { z } from "zod";

/**
 * Eval-platform vocabulary: the pairwise comparison of two agent variants over
 * the same URL. Leaf enum module — no imports from db/schema, api, report or
 * queue.
 */

// Which side of a pairwise comparison won. `tie` is a real verdict, not a
// missing one: it is what a judge records when the two outputs are
// indistinguishable AND what the aggregator records when the two
// position-swapped passes disagree.
export const EVAL_WINNERS = ["a", "b", "tie"] as const;
export const EVAL_WINNER = {
  A: "a",
  B: "b",
  TIE: "tie",
} as const satisfies Record<string, EvalWinner>;
export const EvalWinnerSchema = z.enum(EVAL_WINNERS);
export type EvalWinner = z.infer<typeof EvalWinnerSchema>;

// Who produced a judgment. `llm` is the judge worker; `human` is an analyst
// overriding or seeding a verdict through the admin surface.
export const EVAL_JUDGE_KINDS = ["llm", "human"] as const;
export const EVAL_JUDGE_KIND = {
  LLM: "llm",
  HUMAN: "human",
} as const satisfies Record<string, EvalJudgeKind>;
export const EvalJudgeKindSchema = z.enum(EVAL_JUDGE_KINDS);
export type EvalJudgeKind = z.infer<typeof EvalJudgeKindSchema>;

// The order the two variants' outputs were presented to the judge in one pass.
// `ab` presents variant a first, `ba` presents variant b first. Every LLM
// judgment runs both, because a judge that quietly favours whatever it read
// first would otherwise decide the comparison by position alone.
export const EVAL_JUDGE_PASS_ORDERS = ["ab", "ba"] as const;
export const EVAL_JUDGE_PASS_ORDER = {
  AB: "ab",
  BA: "ba",
} as const satisfies Record<string, EvalJudgePassOrder>;
export const EvalJudgePassOrderSchema = z.enum(EVAL_JUDGE_PASS_ORDERS);
export type EvalJudgePassOrder = z.infer<typeof EvalJudgePassOrderSchema>;

// Which side of the pair a variant occupies for a whole batch. `a` is the
// control and `b` the candidate, fixed at batch creation so every comparison
// in the batch pits the same two configurations against each other. This is
// NOT the presentation order the judge sees — that is EVAL_JUDGE_PASS_ORDER,
// and it is swapped deliberately on every comparison.
export const EVAL_VARIANT_SLOTS = ["a", "b"] as const;
export const EVAL_VARIANT_SLOT = {
  A: "a",
  B: "b",
} as const satisfies Record<string, EvalVariantSlot>;
export const EvalVariantSlotSchema = z.enum(EVAL_VARIANT_SLOTS);
export type EvalVariantSlot = z.infer<typeof EvalVariantSlotSchema>;

// A batch finishes exactly once, either way: `completed` when every comparison
// reached a judgment, `failed` when any comparison could not. A failed batch is
// still a finished batch with real judgments in it — the status says the run
// was not clean, not that its results were discarded.
export const EVAL_BATCH_STATUSES = ["running", "completed", "failed"] as const;
export const EVAL_BATCH_STATUS = {
  RUNNING: "running",
  COMPLETED: "completed",
  FAILED: "failed",
} as const satisfies Record<string, EvalBatchStatus>;
export const EvalBatchStatusSchema = z.enum(EVAL_BATCH_STATUSES);
export type EvalBatchStatus = z.infer<typeof EvalBatchStatusSchema>;

/**
 * One comparison's progress through the pipeline it drives twice.
 *
 * `pending_capture` — run A is analysing; run B cannot start until run A's
 *   fetch is ready, because run B reuses that capture rather than buying its
 *   own. This is the only state in which run B does not exist.
 * `awaiting_completion` — both runs exist and are working. There is no separate
 *   "run B started" state: constructing run B and starting to wait on the pair
 *   are the same instant.
 * `judge_requested` — both runs completed and eval.judge.requested is on the
 *   queue. Re-publishes by the backstop sweeper stay in this state.
 * `judged` / `failed` — terminal.
 */
export const EVAL_COMPARISON_STATUSES = [
  "pending_capture",
  "awaiting_completion",
  "judge_requested",
  "judged",
  "failed",
] as const;
export const EVAL_COMPARISON_STATUS = {
  PENDING_CAPTURE: "pending_capture",
  AWAITING_COMPLETION: "awaiting_completion",
  JUDGE_REQUESTED: "judge_requested",
  JUDGED: "judged",
  FAILED: "failed",
} as const satisfies Record<string, EvalComparisonStatus>;
export const EvalComparisonStatusSchema = z.enum(EVAL_COMPARISON_STATUSES);
export type EvalComparisonStatus = z.infer<typeof EvalComparisonStatusSchema>;

// The comparison states a sweep can still move. Everything else is terminal.
export const ACTIVE_EVAL_COMPARISON_STATUSES = [
  "pending_capture",
  "awaiting_completion",
  "judge_requested",
] as const satisfies readonly EvalComparisonStatus[];
